import { Truck, Banknote, CreditCard, MessageCircle, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { FadeUp, MaskLine } from "@/components/Reveal";
import { useLang } from "@/context/LangContext";
import { useStore } from "@/context/StoreContext";

function Tile({ icon: Icon, title, desc, i, wide, testid }) {
  return (
    <motion.div
      data-testid={testid}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative flex min-h-[220px] flex-col justify-between overflow-hidden border border-[#241914]/12 bg-[#F7F3EB] p-8 transition-colors duration-700 hover:bg-[#0B0B0C] ${wide ? "sm:col-span-2" : ""}`}
    >
      <div className="flex items-start justify-between">
        <Icon className="h-6 w-6 text-[#8a6d35] transition-colors duration-700 group-hover:text-[#C6A76A]" strokeWidth={1.1} />
        <span className="font-latin text-xs tracking-[0.3em] text-[#241914]/45 transition-colors duration-700 group-hover:text-[#F7F3EB]/50">0{i + 1}</span>
      </div>
      <div className="mt-10">
        <h3 className="font-display text-2xl text-[#0B0B0C] transition-colors duration-700 group-hover:text-[#F7F3EB] sm:text-3xl">{title}</h3>
        <p className="mt-3 max-w-sm text-sm leading-relaxed text-[#241914]/75 transition-colors duration-700 group-hover:text-[#F7F3EB]/75">{desc}</p>
      </div>
    </motion.div>
  );
}

export function Services() {
  const { t, rtl } = useLang();
  const { settings } = useStore();
  const tiles = [
    { k: "delivery", icon: Truck, show: true },
    { k: "cod", icon: Banknote, show: settings?.cod_enabled },
    { k: "card", icon: CreditCard, show: settings?.card_enabled },
    { k: "wa", icon: MessageCircle, show: settings?.whatsapp_enabled && settings?.whatsapp_number },
    { k: "curated", icon: Sparkles, show: true },
  ].filter((x) => x.show);

  return (
    <section id="service" data-testid="services-section" className="bg-[#E8DFD0] text-[#0B0B0C]">
      <div className="mx-auto max-w-[1440px] px-5 py-28 sm:px-10 lg:py-40">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <FadeUp className="flex items-center gap-4">
              <span className="font-latin text-xs tracking-[0.3em] text-[#241914]/60">03</span>
              <span className="h-px w-10 bg-[#C6A76A]" />
              <span className="eyebrow !text-[#8a6d35]">{t("services_over")}</span>
            </FadeUp>
            <h2 className={`font-display mt-8 ${rtl ? "text-4xl leading-[1.6] sm:text-5xl" : "text-4xl leading-[1.05] sm:text-6xl"}`}>
              <MaskLine inView>{t("services_title")}</MaskLine>
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-px sm:grid-cols-2 lg:col-span-8">
            {tiles.map((x, i) => (
              <Tile
                key={x.k}
                testid={`service-tile-${x.k}`}
                i={i}
                icon={x.icon}
                title={t(`svc_${x.k}_t`)}
                desc={t(`svc_${x.k}_d`)}
                wide={tiles.length % 2 === 1 && i === tiles.length - 1}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
